/**
 * Cable knit charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cableKnitChartsPack: ToilePack = {
    slug: 'cable-knit-charts',
    category: 'craft',
    name: 'Cable knit charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'rope', label: 'Rope cable', subject: 'a cable knit chart of a simple rope cable six stitches wide, the crossing symbol every sixth row, two purl stitches either side, the rows numbered up the right edge'},
        {key: 'honeycomb', label: 'Honeycomb', subject: 'a cable chart of an allover honeycomb pattern, small crossings leaning left and right in alternating rows so they form linked cells, the repeat boxed'},
        {key: 'aran', label: 'Aran panel', subject: 'a cable chart of an Aran sweater panel, a central diamond with seed stitch inside, flanked by two rope cables and a twisted stitch column on each side'},
    ],
    styles: [
        {key: 'standard', label: 'Standard symbols', style: 'crisp square grid, standard crossing symbols drawn as slanted bars across their squares, purl dots on the background', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'chunky', label: 'Chunky grid', style: 'large squares with a thick grid line and heavy crossing symbols, purl background clearly dotted', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'repeat', label: 'Tinted repeat', style: 'symbol grid with the cable columns tinted pale so the crossing path stands out from the purl ground', palette: 'black symbols on pure white with the cable columns tinted pale', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['baby sweater', 'hat', 'blanket'],
    directionHint: 'try: baby sweater',
    seo: {
        title: 'Cable knit charts: rope, honeycomb, Aran panel',
        metaDescription: 'Nine cable knit charts: a rope cable, an allover honeycomb and an Aran panel, in standard, chunky and tinted grids.',
        h1: 'Cable knit charts: a rope cable, honeycomb and Aran panel',
        tagline: 'Crossing symbols on a purl background, with the crossing rows easy to find.',
        intro: 'Cables are the stitches that cross over one another in Aran sweaters and chunky blankets, and a chart is the clearest way to follow them. These nine cover a rope cable, an allover honeycomb and an Aran panel. Each comes as a standard chart, a chunky grid, or a grid with the cable columns tinted. Type a different cable and the same style carries it.',
        useCases: [
            'a chunky blanket panel',
            'a baby sweater yoke',
            'a hat with a cable band',
            'a practice chart for a first cable',
        ],
        faq: [
            {q: 'What are the dots on the background?', a: 'Dots mark purl stitches, the ground against which the crossing cable stands out.'},
            {q: 'Where do the crossings happen?', a: 'Crossings are marked in the rows where the cable turns. The rope cable crosses every sixth row.'},
            {q: 'Which style makes the cable path easiest to follow?', a: 'The tinted grid, where the cable columns are pale and the crossing path stands out.'},
        ],
    },
}
